// ======================================================================
// File: PurchaseRequestFormDrawer.jsx
// Module: PPE → Procurement → Purchase Request
//
// FIXED VERSION
// - No auto draft creation
// - Proper validation
// - Correct drawer classes
// - Stable workflow
// - Department dropdown integration (using existing API)
// ======================================================================

import React, { useEffect, useState } from "react";

import purchaseRequestService from "../services/purchaseRequestService";

// ✅ NEW: Department service for dropdown
import departmentService from "../services/departmentService";

import ConsolidateIndentDrawer from "./ConsolidateIndentDrawer";
import PRLineTable from "./PRLineTable";

import "../pr-ui.css";

export default function PurchaseRequestFormDrawer({
  open,
  recordId,
  onClose,  
  onSaved,
}) {

  const [form, setForm] = useState({
    pr_no: "",
    pr_date: "",
    department_id: "",
    remarks: "",
  });

  const [prId, setPrId] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConsolidation, setShowConsolidation] = useState(false);

  // ✅ NEW: Departments state
  const [departments, setDepartments] = useState([]);

  // ============================================================== 
  // INITIALIZE DRAWER
  // ============================================================== 
  useEffect(() => {
  if (open) {

    loadDepartments();

    if (recordId) {
      loadExistingPR();
    } else {
      initializeDrawer();
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open, recordId]);

  const initializeDrawer = () => {
    setForm({
       pr_no: "",
      pr_date: "",
      department_id: "",
      remarks: "",
    });
    setLines([]);
    setPrId(null);
  };

  const loadExistingPR = async () => {
  try {

    setLoading(true);

    const res =
      await purchaseRequestService.getPurchaseRequestById(
        recordId
      );

    const pr = res?.data?.data;

    if (!pr) {
      throw new Error("Purchase Request not found");
    }

    setPrId(pr.id);

    setForm({
      pr_no: pr.pr_no || "",
      pr_date: pr.pr_date || "",
      department_id: pr.department_id || "",
      remarks: pr.remarks || "",
    });

    setLines(pr.lines || []);

  } catch (err) {

    console.error(
      "Failed to load purchase request",
      err
    );

    alert(
      "Failed to load purchase request"
    );

  } finally {
    setLoading(false);
  }
};

  // ============================================================== 
  // LOAD DEPARTMENTS FOR DROPDOWN
  // ============================================================== 
  const loadDepartments = async () => {
  try {

    // Try both common patterns to be safe
    const tenantId =
      localStorage.getItem("tenant_id") ||
      localStorage.getItem("tenantId");

    const locationId =
      localStorage.getItem("location_id") ||
      localStorage.getItem("locationId");

    const params = {
      tenant_id: tenantId,
      location_id: locationId
    };

    const res = await departmentService.getDepartments(params);

    setDepartments(res?.data?.data || []);

  } catch (err) {
    console.error("Failed to load departments", err);
  }
};



  // ============================================================== 
  // OPEN CONSOLIDATION (Creates PR only when required)
  // ============================================================== 
  const handleOpenConsolidation = async () => {

    if (!form.pr_date) {
      alert("Please select PR Date");
      return;
    }

    if (!form.department_id) {
      alert("Please select Department");
      return;
    }

    try {
      setLoading(true);

      // Create PR header only if not already created
      if (!prId) {

        // Ensure department_id is sent as integer
        const payload = {
          ...form,
          department_id: parseInt(form.department_id)
        };

        const response = await purchaseRequestService.createPurchaseRequest(payload);
        const data = response?.data?.data;

        if (!data || !data.id) {
          throw new Error("Invalid response from server while creating PR");
        }

        setPrId(data.id);
              setForm((prev) => ({
        ...prev,
        pr_no: data.pr_no || "",
      }));
      }

      setShowConsolidation(true);

    } catch (err) {
      console.error("Failed to create PR draft", err);

      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create purchase request draft";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ============================================================== 
  // CONSOLIDATE INDENTS INTO PR
  // ============================================================== 
  const handleConsolidate = async (selectedLines) => {
    try {
      setLoading(true);

      await purchaseRequestService.consolidateIndentLines(
        prId,
        selectedLines
      );

      const refreshed = await purchaseRequestService.getPurchaseRequestById(prId);
      setLines(refreshed?.data?.data?.lines || []);

      setShowConsolidation(false);

    } catch (err) {
      console.error("Consolidation failed", err);

      const message =
        err?.response?.data?.message ||
        "Failed to consolidate indent lines";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ============================================================== 
  // SUBMIT PR
  // ============================================================== 
  const handleSubmit = async () => {

    if (!form.pr_date) {
      alert("Please select PR Date");
      return;
    }

    if (!form.department_id) {
      alert("Please select Department");
      return;
    }

    if (!prId) {
      alert("Please add indent lines before submitting PR");
      return;
    }

    if (!lines.length) {
      alert("Please add at least one material line");
      return;
    }

    try {
      setLoading(true);

      //await purchaseRequestService.submitPurchaseRequest(prId);
      await purchaseRequestService.submitPurchaseRequest(
        prId,
        {
          remarks: form.remarks
        }
      );
      alert("Purchase Request submitted successfully");

      onSaved?.();

    } catch (err) {
      console.error("Submit failed", err);

      const message =
        err?.response?.data?.message ||
        "Failed to submit purchase request";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ============================================================== 
  // RENDER
  // ============================================================== 
  if (!open) return null;

  return (
    <div className="pr-drawer open">

      {/* HEADER */}
      <div className="pr-drawer-header">
        <h3>Create Purchase Request</h3>

        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="pr-drawer-body">

        {loading && (
          <div style={{ marginBottom: "10px", fontSize: "13px" }}>
            Processing...
          </div>
        )}

        <div className="form-grid">
        {prId && (
            <div className="form-field">
              <label>PR Number</label>
              <input
                type="text"
                className="pr-input"
                value={form.pr_no || ""}
                readOnly
              />
            </div>
          )}

          <div className="form-field">
            <label>PR Date</label>
            <input
              type="date"
              className="pr-input"
              value={form.pr_date}
              readOnly={Boolean(prId)}
              onChange={(e) =>
                setForm({ ...form, pr_date: e.target.value })
              }
            />
          </div>

          <div className="form-field">
            <label>Department</label>

            {/* ✅ REPLACED TEXT INPUT WITH DROPDOWN */}
            <select
              className="pr-select"
              value={form.department_id}
              disabled={Boolean(prId)}
              onChange={(e) =>
                setForm({ ...form, department_id: e.target.value })
              }
            >
              <option value="">Select Department</option>

              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}

            </select>

          </div>

          <div className="form-field full">
            <label>Remarks</label>
            <textarea
              className="pr-input"
              placeholder="Enter remarks"
              value={form.remarks}
              onChange={(e) =>
                setForm({ ...form, remarks: e.target.value })
              }
            />
          </div>

        </div>

        {/* LINE ITEMS */}
        <div className="section-header">
          <h4>Consolidated Materials</h4>

          <button
            className="btn-primary"
            onClick={handleOpenConsolidation}
            disabled={loading}
          >
            + Add Indent Lines
          </button>
        </div>

        <PRLineTable
          prId={prId}
          lines={lines}
        />

      </div>

      {/* FOOTER */}
      <div className="pr-drawer-footer">

        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!lines.length || loading}
        >
          Submit PR
        </button>

      </div>

      {/* CONSOLIDATION DRAWER */}
      {showConsolidation && (
        <ConsolidateIndentDrawer
          open={showConsolidation}
          departmentId={form.department_id}
          onClose={() => setShowConsolidation(false)}
          onConfirm={handleConsolidate}
        />
      )}

    </div>
  );
}
